import { AxiosProgressEvent, CancelToken } from 'axios';

import { AIModel, AIStatus } from '@mygpt/app/apis';
import { Http } from '@mygpt/app/http';
import { I18N_LANG_MAP } from '@mygpt/app/chat/constant';
import { shouldUseChunkUploadForFiles, uploadWithChunks, ChunkUploadProgress } from './chunk-uploader';

/**
 * ready: 等待学习
 * invalid: 无效文件（文件损坏、密码加密、网页404/403等）
 */
export type ListFileStatus = 'crawled' | 'process' | 'complete' | 'fail' | 'exceeded' | 'deleted' | 'ready' | 'invalid';
export type AIType = 'public' | 'private' | 'trending';
export type RobotType = 'rag' | 'digital_human' | 'agent' | 'catalog_agent' | 'general_agent' | 'chatbot';

export interface ListPageParams {
  query?: string;
  file_status?: ListFileStatus;
  start_time?: string;
  end_time?: string;
  page?: number;
  size?: number;
  order_by?: string;
  // 公司ID：不传或为空表示个人资产，传组织ID表示组织资产
  company_id?: string;
}

export interface ListResponse {
  page: number;
  pages: number;
  size: number;
  total: number;
}

interface DatasetsList extends ListResponse {
  items: DatasetsInfo[];
}

interface RobotInfo {
  id: string;
  created_at: string;
  name: string;
  discrible: string;
  subject_name: string;
  prompt: string;
  ai_type: AIType;
  ai_model: AIModel;
  ai_status: AIStatus;
  user_id: string;
}

export interface DatasetsInfo {
  id: string;
  created_at: string;
  updated_at: string;
  data_status: 'init' | 'ready' | 'frozen' | 'cloning_target' | 'cloning_source'; // frozen: 冻结,
  name: string;
  description: string;
  metadata: {
    embedding_model_name: string;
    faq_vector_storage: string;
    vector_storage: string;
  };
  user_id: string;
  robots: RobotInfo[];
  clone_progress: {
    done: number;
    remaining: number;
    total: number;
  };
  error?: string;
  permission_type?: string;
}

export interface DatasetsInfoWithProgress extends DatasetsInfo {
  total_resources: number;
  status: {
    learned_count: number;
    learning_count: number;
    pending_count: number;
    failed_count: number;
    unavailable_count: number;
  };
}

export interface DatasetsFileType {
  api: number;
  faq: number;
  file: number;
  gitbook: number;
  property: number;
  sitemap: number;
  website: number;
  integration: {
    lark: number;
    web_connector: number;
    gbase_project: number;
  };
}

export interface AddDatasetParams {
  name: string;
  description: string;
  embedding_model_name: string;
  // 资产范围：null/不传表示个人资产，组织ID表示组织资产
  company_id?: string | null;
}

interface UpdateDatasetsParams extends AddDatasetParams, HandleByIdParams {}

interface DelDatasetsParams extends HandleByIdParams {
  file_ids: string[];
}

interface CloneDatasetsParams extends HandleByIdParams {
  target_embedding_model: string;
}

interface HandleByIdParams {
  id: string;
  language: string;
}

export type DataSourcesType = 'file' | 'website' | 'sitemap' | 'gitbook' | 'faq' | 'api' | 'document' | 'integration';
export type IntegrationSubType = 'lark';

interface ListPageByIdParams extends ListPageParams {
  id: string;
  file_type: DataSourcesType;
  source_type?: IntegrationSubType;
  learn_type_filter?: string;
  integration_rule_id?: string; // 集成类型时，需要传入规则id
}

interface LarkFile {
  name: string;
  type: string;
  token: string;
  parent_token: string;
  created_time: string;
  modified_time: string;
  owner_id: string;
  url: string;
}

export type FileStatus = 'crawled' | 'process' | 'complete' | 'fail' | 'exceeded' | 'ready' | 'invalid';
export type SourceType = 'web_connector' | 'lark';
export interface DatasetsFilesInfo {
  id: string;
  created_at: string;
  updated_at: string;
  filename: string;
  file_type: string;
  file_size: number;
  file_status: FileStatus;
  failed_reason: string;
  index_ids: string;
  content_hash: string;
  dataset_id: string;
  tokens: number;
  token_count: number;
  title: string | null;
  characters_count: number;
  lark_file?: LarkFile;
  learn_type: number;
  source_type: SourceType | null;
}

export interface DatasetsFilesList extends ListResponse {
  items: DatasetsFilesInfo[];
}

interface AddDatasetsFilesParams {
  id: string;
  formData: FormData;
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void;
}

interface UpdateDatasetsFilesParams {
  datasetId: string;
  id: string;
  title: string;
}

interface AddDatasetsLinksParams {
  id: string;
  links_type: DataSourcesType;
  links: string[];
  spa: boolean;
}

interface DatasetsConfigsParams {
  id: string;
  metadata: {
    [key: string]: string;
  };
}

interface RefreshDatasetsFileParams {
  datasetId: string;
  fileIds: string[];
  learn_type?: number;
}

interface RefreshDatasetsIntegrationFileParams extends RefreshDatasetsFileParams {
  integration_rule_id: string;
}

/**
 * 数据集列表
 * @param params
 * @returns
 */
export const getDatasetsListDatasets = (params: ListPageParams) => {
  const { company_id, query, ...rest } = params;
  const { get } = Http.use();
  // 后端 /datasets 接口的搜索参数名是 search，这里把前端通用的 query 映射过去
  const queryParams = {
    ...rest,
    ...(query && { search: query }),
    ...(company_id && { company_id }),
  };
  return get<DatasetsList>(`/datasets`, {
    params: queryParams,
  });
};

/**
 * 获取单个数据集
 * @param params
 * @returns
 */
export const getDatasets = (params: HandleByIdParams) => {
  const { id } = params;
  const { get } = Http.use();
  return get<DatasetsInfo>(`/datasets/${id}`);
};

/**
 * 添加数据集
 * @param params
 * @returns
 */
export const addDatasets = (params: AddDatasetParams) => {
  const { company_id, ...rest } = params;
  const { post } = Http.use();
  // 处理 company_id：如果为 null 则不传，如果有值则传入
  const bodyWithCompanyId = company_id ? { ...rest, company_id } : rest;
  return post<DatasetsInfo>(`/datasets`, bodyWithCompanyId);
};

/**
 * 更新数据集
 * @param params
 * @returns
 */
export const updateDatasets = (params: UpdateDatasetsParams) => {
  const { id, ...rest } = params;
  const { put } = Http.use();
  return put<DatasetsInfo>(`/datasets/${id}`, rest);
};

/**
 * 删除数据集
 * @param params
 * @returns
 */
export const delDatasets = (params: HandleByIdParams) => {
  const { id } = params;
  const { delete: _delete } = Http.use();
  return _delete(`/datasets/${id}`);
};

/**
 * 克隆数据集
 */
export const cloneDatasets = (params: CloneDatasetsParams) => {
  const { id, target_embedding_model } = params;
  const { post } = Http.use();
  return post<DatasetsInfo>(`/datasets/${id}/clone`, {
    target_embedding_model,
  });
};

/**
 * 获取数据集文件类型概览
 * @param params
 * @returns
 */
export const getDatasetsFilesType = (params: HandleByIdParams) => {
  const { id, language } = params;
  const { get } = Http.use();
  return get<DatasetsFileType>(`/datasets/${id}/sources/counts`, {
    params: {
      language: I18N_LANG_MAP[language],
    },
  });
};

/**
 * 获取数据源列表
 * @param params
 * @returns
 */
export const getDatasetsFilesList = (params: ListPageByIdParams, cancelToken?: CancelToken) => {
  const { id, ...rest } = params;
  const { get } = Http.use();
  return get<DatasetsFilesList>(`/datasets/${id}/files`, {
    params: rest,
    cancelToken: cancelToken,
  });
};

/**
 *添加文件数据源
 * @param params
 * @returns
 */
export const addDatasetsFiles = (params: AddDatasetsFilesParams) => {
  const { formData, id, onUploadProgress } = params;

  // 从 FormData 中提取文件
  const files = formData.getAll('files') as File[];

  // 判断是否需要分片上传
  if (files && files.length > 0 && shouldUseChunkUploadForFiles(files)) {
    // 使用分片上传
    return uploadWithChunks({
      datasetId: id,
      files,
      formData: formData, // 传递原始 FormData，包含其他字段
      onProgress: (progress: ChunkUploadProgress) => {
        // 转换为 AxiosProgressEvent 格式，保持兼容
        onUploadProgress({
          loaded: progress.uploadedChunks,
          total: progress.totalChunks,
        } as AxiosProgressEvent);
      },
    }).then((result) => {
      console.log('[datasets.ts] 分片上传完成:', result);
      console.log('[datasets.ts] 返回数据:', result.data);
      if (!result.success) {
        throw new Error(result.error || '分片上传失败');
      }
      const files = result.data as DatasetsFilesInfo[];
      console.log('[datasets.ts] 文件列表:', files);
      // ✅ 修复：返回兼容 AxiosResponse 结构的对象，保持与普通上传一致
      return { data: files };
    });
  }

  // 原有流程：普通上传
  const { post } = Http.use();
  return post<DatasetsFilesInfo[]>(`/datasets/${id}/files`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onUploadProgress,
  });
};

/**
 * 更新文件数据源
 * @param params
 * @returns
 */
export const updateDatasetsFiles = (params: UpdateDatasetsFilesParams) => {
  const { datasetId, ...rest } = params;
  const { put } = Http.use();
  return put<DatasetsFilesInfo>(`/datasets/${datasetId}/files`, rest);
};

/**
 * 删除文件数据源
 * @param params
 * @returns
 */
export const delDatasetsFiles = (params: DelDatasetsParams) => {
  const { id, ...rest } = params;

  const { delete: _delete } = Http.use();
  return _delete(`/datasets/${id}/files`, {
    data: rest,
  });
};

/**
 * 添加网站数据源
 * @param params
 * @returns
 */
export const addDatasetsLinks = (params: AddDatasetsLinksParams) => {
  const { id, ...rest } = params;
  const { post } = Http.use();
  return post(`/datasets/${id}/links`, rest, {
    validateStatus: function (status) {
      return status >= 200 && status < 300;
    },
  });
};

/**
 * 添加网站数据源
 * @param params
 * @returns
 */
export const updateDatasetsConfigs = (params: DatasetsConfigsParams) => {
  const { id, ...rest } = params;
  const { post } = Http.use();
  return post<DatasetsFilesList>(`/datasets/${id}/configs`, {
    params: {
      ...rest,
    },
  });
};

/**
 * 重新学习网站
 * @param params
 * @returns
 */
export const refreshDatasetsWebsite = (params: RefreshDatasetsFileParams) => {
  const { datasetId, fileIds } = params;
  const { put } = Http.use();
  return put(`/datasets/${datasetId}/files/refresh_list`, {
    file_id_list: fileIds,
  });
};

/**
 * 重新学习文件
 * @param params
 * @returns
 */
export const refreshDatasetsFile = (params: RefreshDatasetsFileParams) => {
  const { datasetId, fileIds, learn_type } = params;
  const { put } = Http.use();
  return put(`/datasets/${datasetId}/upload_files/refresh_list`, {
    file_id_list: fileIds,
    learn_type,
  });
};

/**
 * 集成下面从三方源导入的文件的重新学习接口
 * 后端会先拉取三方源的文件，然后重新学习
 * @param params
 * @returns
 */
export const refreshDatasetsIntegrationFile = (params: RefreshDatasetsIntegrationFileParams) => {
  const { datasetId, fileIds, learn_type, integration_rule_id } = params;
  const { put } = Http.use();
  return put(`/datasets/${datasetId}/lark_files/refresh_list`, {
    file_id_list: fileIds,
    learn_type,
    integration_rule_id,
  });
};
